// Adjusted by GPT-4o with canvas.
import html from './template.html';

const template = document.createElement('template');
template.innerHTML = html;

class ModalOverlay extends HTMLElement {
    static get observedAttributes() {
        return ['open'];
    }

    static get tagName() {
        return 'modal-overlay';
    }

    constructor() {
        super();

        this.attachShadow({ mode: 'open' });
        this.shadowRoot.append(template.content.cloneNode(true));

        this._focusableElements = [];
        this._onKeyDown = this._onKeyDown.bind(this);
        this._trapFocus = this._trapFocus.bind(this);
        this._triggeringElement = null;  // To store the element that triggered the modal
    }

    get open() {
        return this.hasAttribute('open');
    }

    set open(value) {
        if (value) {
            this._triggeringElement = document.activeElement; // Store the element that triggered the modal
            this.setAttribute('open', '');
            this.adjustTitle();
            this._setupFocusTrap();
        } else {
            this.removeAttribute('open');
            this._teardownFocusTrap();
            if (this._triggeringElement) {
                this._triggeringElement.focus();  // Return focus to the triggering element
            }
        }
    }

    get maxWidth() {
        return this.getAttribute('max-width');
    }

    get title() {
        return this.getAttribute('modal-title');
    }

    set title(value) {
        this.setAttribute('modal-title', value);
    }

    get content() {
        return this.shadowRoot.querySelector('.content-wrapper');
    }

    get closeElement() {
        return this.shadowRoot.querySelector('.close-btn');
    }

    get backdrop() {
        return this.shadowRoot.querySelector('.backdrop');
    }

    adjustTitle() {
        if (this.title) {
            const titleElement = this.shadowRoot.querySelector('.title');
            if (titleElement) {
                titleElement.innerText = this.title;
            }
        }
    }

    attributeChangedCallback() {
        this.dispatchEvent(
            new CustomEvent('modaloverlaychange', {
                bubbles: true,
                detail: {
                    isOpen: this.open,
                },
            })
        );
    }

    connectedCallback() {
        if (this.backdrop) {
            this.backdrop.addEventListener('click', () => {
                this.open = false;
            });
        }
        if (this.closeElement) {
            this.closeElement.addEventListener('click', () => {
                this.open = false;
            });
        }
    }

    disconnectedCallback() {
        this._teardownFocusTrap();
    }

    _onKeyDown(event) {
        const escKeyCode = 27;

        if (event.keyCode === escKeyCode && this.open) {
            this.open = false;
            event.preventDefault();
        } else if (event.key === 'Tab' && this.open) {
            this._trapFocus(event);
        }
    }

    _trapFocus(event) {
        if (!this.open) {
            return;
        }

        const focusableElements = this._focusableElements;
        if (focusableElements.length === 0) {
            event.preventDefault();
            return;
        }

        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];
        const { target } = event;

        // Handle tabbing forwards and backwards
        if (event.key === 'Tab') {
            if (event.shiftKey && target === firstFocusable) {
                // If Shift + Tab is pressed and the first focusable element is reached
                event.preventDefault();
                lastFocusable.focus();
            } else if (!event.shiftKey && target === lastFocusable) {
                // If Tab is pressed and the last focusable element is reached
                event.preventDefault();
                firstFocusable.focus();
            } else {
                // Ensure tabbing within modal if the focus is on any other element
                if (!focusableElements.includes(target)) {
                    event.preventDefault();
                    firstFocusable.focus();
                }
            }
        }
    }

    _setupFocusTrap() {
        // Collect all focusable elements inside the modal
        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'textarea:not([disabled])',
            'input[type="text"]:not([disabled])',
            'select:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ];

        this._focusableElements = Array.from(
            this.shadowRoot.querySelectorAll(focusableSelectors.join(','))
        ).filter((el) => el.offsetParent !== null);

        if (this.closeElement && !this._focusableElements.includes(this.closeElement)) {
            this._focusableElements.push(this.closeElement);
        }

        // Add a keydown listener to the document for trapping focus and handling Escape key
        document.addEventListener('keydown', this._onKeyDown);

        // Set initial focus to the first focusable element in the modal
        if (this._focusableElements.length > 0) {
            this._focusableElements[0].focus();
        }
    }

    _teardownFocusTrap() {
        // Remove keydown listener from the document
        document.removeEventListener('keydown', this._onKeyDown);
        this._focusableElements = [];
    }
}

customElements.define(ModalOverlay.tagName, ModalOverlay);



// WEBPACK FOOTER //
// ./scripts/web-components/modal-overlay/index.js